import React, { useState, useEffect, useRef } from "react";
import { createChart, CrosshairMode } from 'lightweight-charts';
import ApiClient from "../api/ApiClient";

// ── Performance chart modal ─────────────────────────────────────────────────
function PerformanceChartModal({ modelId, modelName, tag, onClose }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [history, setHistory] = useState(null);
  const chartContainerRef = useRef(null);
  const chartRef = useRef(null);

  useEffect(() => {
    ApiClient.getModelDefaultPerformanceCalculationPortfolioHistory(modelId, tag)
      .then(data => { setHistory(data || []); setLoading(false); })
      .catch(err => { setError(err.message || 'Failed to load'); setLoading(false); });
  }, [modelId, tag]);

  useEffect(() => {
    if (!history || history.length === 0 || !chartContainerRef.current) return;
    if (chartRef.current) { chartRef.current.remove(); chartRef.current = null; }

    const chart = createChart(chartContainerRef.current, {
      width: chartContainerRef.current.clientWidth,
      height: 320,
      layout: { background: { color: '#ffffff' }, textColor: '#333' },
      grid: { vertLines: { color: '#eee' }, horzLines: { color: '#eee' } },
      timeScale: { borderColor: '#ccc' },
      crosshair: { mode: CrosshairMode.Normal },
    });
    chartRef.current = chart;

    const series = chart.addLineSeries({ color: '#0d6efd', lineWidth: 2, title: 'Portfolio Value' });
    const data = history
      .map(p => ({ time: p.Date.substring(0, 10), value: p.TotalValue }))
      .filter((p, i, arr) => i === 0 || p.time !== arr[i - 1].time);
    series.setData(data);
    chart.timeScale().fitContent();

    const handleResize = () => {
      if (chartContainerRef.current && chartRef.current)
        chartRef.current.applyOptions({ width: chartContainerRef.current.clientWidth });
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      if (chartRef.current) { chartRef.current.remove(); chartRef.current = null; }
    };
  }, [history]);

  const first = history && history.length ? history[0].TotalValue : null;
  const last = history && history.length ? history[history.length - 1].TotalValue : null;
  const gain = first != null && last != null ? last - first : null;
  const gainPct = gain != null && first > 0 ? (gain / first) * 100 : null;

  return (
    <div className="modal d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-xl">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              Performance Chart — {modelName || `Model #${modelId}`}
              {tag && <span className="badge bg-info text-dark ms-2">tag: {tag}</span>}
            </h5>
            <button type="button" className="btn-close" onClick={onClose} />
          </div>
          <div className="modal-body">
            {loading && <div className="text-center py-4"><div className="spinner-border" /></div>}
            {error && <div className="alert alert-warning">{error}</div>}
            {!loading && !error && history && history.length === 0 && (
              <p className="text-muted">No portfolio history available for this model.</p>
            )}
            {!loading && !error && gain != null && (
              <div className="row g-2 mb-3">
                <div className="col-4">
                  <div className="card text-center">
                    <div className="card-body py-2">
                      <div className="text-muted small">Initial</div>
                      <div className="fw-bold">${first?.toLocaleString('en-US', { minimumFractionDigits: 2 })}</div>
                    </div>
                  </div>
                </div>
                <div className="col-4">
                  <div className="card text-center">
                    <div className="card-body py-2">
                      <div className="text-muted small">Final</div>
                      <div className="fw-bold">${last?.toLocaleString('en-US', { minimumFractionDigits: 2 })}</div>
                    </div>
                  </div>
                </div>
                <div className={`col-4`}>
                  <div className={`card text-center border-${gain >= 0 ? 'success' : 'danger'}`}>
                    <div className="card-body py-2">
                      <div className="text-muted small">Gain / Loss</div>
                      <div className={`fw-bold text-${gain >= 0 ? 'success' : 'danger'}`}>
                        {gain >= 0 ? '+' : ''}${gain?.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                        {gainPct != null && <span className="ms-1 small">({gainPct >= 0 ? '+' : ''}{gainPct.toFixed(2)}%)</span>}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
            <div ref={chartContainerRef} style={{ width: '100%', height: 320 }} />
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Close</button>
          </div>
        </div>
      </div>
    </div>
  );
}

const pageSizeOptions = [10, 25, 100, 500];

const orderByOptions = [
  { value: "IdDesc", label: "ID (Desc)" },
  { value: "Id", label: "ID (Asc)" },
  { value: "Name", label: "Name (Asc)" },
  { value: "NameDesc", label: "Name (Desc)" },
  { value: "CreateTimeDesc", label: "Create Time (Desc)" },
  { value: "CreateTime", label: "Create Time (Asc)" },
];

const COMBINE_MODE_OPTIONS = [
  { value: 'CbSoM', label: 'cBSoM — unanimous Buy / unanimous Sell / Mixed → Money (default)' },
  { value: 'CBoS',  label: 'cBoS — Buy only when all agree Buy, otherwise Sell' },
  { value: 'CSoB',  label: 'cSoB — Sell only when all agree Sell, otherwise Buy' },
];

// ── Import-zip modal ────────────────────────────────────────────────────────
//
// Wraps the upload-and-import flow in its own component so the parent
// Models page does not have to carry another half-dozen useState slots
// just for this transient form. The modal owns the file picker, the
// pre-submit preview state (kept here so the page can hand the modal
// back into a closed state), and the success / error messaging that
// follows a successful import.
function ImportZipModal({ onClose, onImported }) {
  const [file, setFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  // Track Esc-to-close for consistency with the rest of the page.
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  function handleFileChange(e) {
    const f = e.target.files[0];
    setFile(f || null);
    setError(null);
    setResult(null);
  }

  async function doImport() {
    if (!file) {
      setError("Please choose a zip file first.");
      return;
    }
    setImporting(true);
    setError(null);
    try {
      const response = await ApiClient.importModelsZip(file, file.name);
      setResult(response);
      if (response && typeof onImported === 'function') onImported();
    } catch (err) {
      console.error("Import failed", err);
      setError(err.message || err.toString());
    } finally {
      setImporting(false);
    }
  }

  return (
    <div
      className="modal show"
      tabIndex="-1"
      style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
    >
      <div className="modal-dialog modal-lg">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Import Models</h5>
            <button type="button" className="btn-close" onClick={onClose} />
          </div>
          <div className="modal-body">
            <p className="text-muted">
              Select a zip file produced by the <strong>Export</strong> action.
              All models inside the zip will be created in a single database
              transaction — either every model is added, or none of them are.
            </p>
            <div className="form-group">
              <label className="form-label">Zip file</label>
              <input
                type="file"
                accept=".zip,application/zip,application/x-zip-compressed"
                className="form-control-file"
                onChange={handleFileChange}
                disabled={importing}
              />
              {file && (
                <small className="form-text text-muted d-block mt-1">
                  Selected: {file.name} ({Math.round(file.size / 1024)} KB)
                </small>
              )}
            </div>
            {error && (
              <div className="alert alert-danger mt-3" role="alert">
                <strong>Import failed:</strong> {error}
              </div>
            )}
            {result && (
              <div className="alert alert-success mt-3" role="alert">
                <strong>{result.CreatedCount} model{result.CreatedCount === 1 ? '' : 's'} created.</strong>
                {result.Results && result.Results.length > 0 && (
                  <ul className="mt-2 mb-0">
                    {result.Results.map((r, idx) => (
                      <li key={idx}>
                        {r.ModelName}
                        {r.FileName ? ` (${r.FileName})` : ''}
                        {r.ModelId != null ? ` → #${r.ModelId}` : ''}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
              disabled={importing}
            >
              Close
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={doImport}
              disabled={importing || !file || (result && result.CreatedCount > 0)}
            >
              {importing ? 'Importing...' : (result && result.CreatedCount > 0) ? 'Imported' : 'Import'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export const Models = () => {
  const [name, setName] = useState("");
  const [fileName, setFileName] = useState("");
  const [hasRealtimeCandlesInterval, setHasRealtimeCandlesInterval] = useState("");
  const [orderBy, setOrderBy] = useState("IdDesc");

  const [pageSize, setPageSize] = useState(25);
  const [page, setPage] = useState(0); // zero-based

  const [total, setTotal] = useState(0);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  // Modal state for edit/create
  const [editingModel, setEditingModel] = useState(null);
  const [editName, setEditName] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [editAutomaticExecution, setEditAutomaticExecution] = useState(false);
  const [editSignalTags, setEditSignalTags] = useState([]);
  const [editActiveSignalTag, setEditActiveSignalTag] = useState("");
  const [editFileName, setEditFileName] = useState("");
  const [editFileData, setEditFileData] = useState(null);
  const [editPublic, setEditPublic] = useState(false);
  const [editVisibleToUserIds, setEditVisibleToUserIds] = useState([]);
  const [editRealtimeCandlesAutomaticExecutionIntervalInMinutes, setEditRealtimeCandlesAutomaticExecutionIntervalInMinutes] = useState("");
  const [editCombineMode, setEditCombineMode] = useState("CbSoM");
  const [saving, setSaving] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [usersList, setUsersList] = useState([]);
  const [subscriptions, setSubscriptions] = useState({}); // modelId -> bool
  const [subscriptionLoading, setSubscriptionLoading] = useState({});
  const [perfChartModel, setPerfChartModel] = useState(null); // { id, name }

  // Multi-select for the bulk Export button. Set of selected model ids.
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkExporting, setBulkExporting] = useState(false);
  const [importModalOpen, setImportModalOpen] = useState(false);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, orderBy]);

  useEffect(() => {
    loadUsersList();
  }, []);

  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape' && (editingModel || isCreating)) {
        closeModal();
      }
    };

    if (editingModel || isCreating) {
      document.addEventListener('keydown', handleEscKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [editingModel, isCreating]);

  async function loadUsersList() {
    try {
      const users = await ApiClient.getUsersList();
      setUsersList(users || []);
    } catch (err) {
      console.error("Failed to load users list", err);
    }
  }

  async function fetchData() {
    setLoading(true);
    const body = {
      NamePartial: name || undefined,
      FileNamePartial: fileName || undefined,
      HasRealtimeCandlesAutomaticExecutionInterval: hasRealtimeCandlesInterval === "" ? undefined : hasRealtimeCandlesInterval === "true",
      OrderBy: orderBy,
      Limit: pageSize,
      Offset: page * pageSize,
    };

    try {
      const data = await ApiClient.searchModels(body);
      setItems(data.Items);
      setTotal(data.TotalCount);
      loadSubscriptions(data.Items);
    } catch (err) {
      console.error("Search failed", err);
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }

  function onSearch(e) {
    e.preventDefault();
    setPage(0);
    fetchData();
  }

  function handleFileChange(e) {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const arrayBuffer = event.target.result;
        const bytes = new Uint8Array(arrayBuffer);
        // Convert bytes to hex string for BytesToHexConverter on backend
        const hexString = Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('');
        setEditFileData(hexString);
        setEditFileName(file.name);
        // Signal tags will be re-parsed server-side on save
        setEditSignalTags([]);
        setEditActiveSignalTag("");

        // Convenience: if the user has not typed a Name yet, default it
        // to the picked file's stem (filename without extension). Only
        // auto-populates when the Name field is empty so we never
        // clobber what the user has already typed.
        if (isCreating && !editName.trim()) {
          const stem = file.name.replace(/\.[^./\\]+$/, '');
          if (stem) {
            setEditName(stem);
          }
        }
      };
      reader.readAsArrayBuffer(file);
    }
  }

  async function loadSubscriptions(modelsList) {
    if (!modelsList || modelsList.length === 0) return;
    const results = await Promise.allSettled(
      modelsList.map(m => ApiClient.getModelSubscription(m.Id).then(v => ({ id: m.Id, subscribed: v })))
    );
    const map = {};
    results.forEach(r => {
      if (r.status === 'fulfilled') {
        map[r.value.id] = r.value.subscribed;
      }
    });
    setSubscriptions(map);
  }

  async function toggleSubscription(model) {
    const isSubscribed = subscriptions[model.Id];
    setSubscriptionLoading(prev => ({ ...prev, [model.Id]: true }));
    try {
      if (isSubscribed) {
        await ApiClient.unsubscribeModel(model.Id);
        setSubscriptions(prev => ({ ...prev, [model.Id]: false }));
      } else {
        await ApiClient.subscribeModel(model.Id);
        setSubscriptions(prev => ({ ...prev, [model.Id]: true }));
      }
    } catch (err) {
      console.error("Subscription toggle failed", err);
      alert("Failed to update subscription: " + (err.message || err));
    } finally {
      setSubscriptionLoading(prev => ({ ...prev, [model.Id]: false }));
    }
  }

  async function downloadModel(model) {
    try {
      const { blob, filename } = await ApiClient.downloadModel(model.Id);

      // Convert blob to base64 data URI. This avoids all browser inconsistencies
      // with percent-decoding of link.download on blob: URLs (Chrome vs Firefox).
      // With a data: URI, link.download is always used as a literal filename string.
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
      const link = document.createElement('a');
      link.href = base64;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      if (err.name === 'AbortError') return;
      console.error('Download failed', err);
      alert('Download failed: ' + (err.message || err));
    }
  }

  async function deleteModel(model) {
    if (!window.confirm(`Are you sure you want to delete model "${model.FileName}"?`)) {
      return;
    }

    try {
      await ApiClient.deleteModel(model.Id);
      setItems(prev => prev.filter(m => m.Id !== model.Id));
      setTotal(prev => prev - 1);
      // Drop the deleted id from the bulk-export selection set so the
      // Export button doesn't reference a row that's no longer on screen.
      setSelectedIds(prev => {
        if (!prev.has(model.Id)) return prev;
        const next = new Set(prev);
        next.delete(model.Id);
        return next;
      });
    } catch (err) {
      console.error("Delete failed", err);
      alert("Delete failed: " + (err.message || err));
    }
  }

  // Selection state is per-page: when the search results change we drop
  // any selected ids that are no longer in the visible list so the
  // Export button never references a stale row.
  useEffect(() => {
    if (!items || items.length === 0) {
      setSelectedIds(prev => prev.size > 0 ? new Set() : prev);
      return;
    }
    const visible = new Set(items.map(m => m.Id));
    setSelectedIds(prev => {
      let changed = false;
      const next = new Set();
      for (const id of prev) {
        if (visible.has(id)) next.add(id);
        else changed = true;
      }
      return changed ? next : prev;
    });
  }, [items]);

  function toggleSelected(id) {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleSelectAll() {
    setSelectedIds(prev => {
      const visibleIds = (items || []).map(m => m.Id);
      const allSelected = visibleIds.length > 0 && visibleIds.every(id => prev.has(id));
      if (allSelected) return new Set();
      return new Set(visibleIds);
    });
  }

  async function bulkExportSelected() {
    if (selectedIds.size === 0) {
      alert("Select at least one model to export");
      return;
    }
    setBulkExporting(true);
    try {
      const ids = Array.from(selectedIds);
      const { blob, filename } = await ApiClient.exportModelsZip(ids);

      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
      const link = document.createElement('a');
      link.href = base64;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error("Bulk export failed", err);
      alert("Bulk export failed: " + (err.message || err));
    } finally {
      setBulkExporting(false);
    }
  }

  function renderTable() {
    if (loading)
      return (
        <p>
          <em>Loading...</em>
        </p>
      );
    if (!items || items.length === 0) return <p>No results</p>;

    return (
      <>
        <table className="table table-striped">
          <thead>
            <tr>
              <th style={{ width: '32px' }}>
                <input
                  type="checkbox"
                  aria-label="Select all models on this page"
                  checked={items.length > 0 && items.every(m => selectedIds.has(m.Id))}
                  ref={el => {
                    // Render an indeterminate state when only some of the
                    // visible rows are selected. The HTML attribute is
                    // read-only — set it via the DOM property each render.
                    if (el) {
                      const allSelected = items.length > 0 && items.every(m => selectedIds.has(m.Id));
                      const noneSelected = items.every(m => !selectedIds.has(m.Id));
                      el.indeterminate = !allSelected && !noneSelected;
                    }
                  }}
                  onChange={toggleSelectAll}
                />
              </th>
              <th>Id</th>
              <th>Name</th>
              <th>Auto Exec</th>
              <th>Public</th>
              <th style={{ width: '300px' }}>File Name</th>
              <th>Signal Tags</th>
              <th>Active Signal Tag</th>
              <th>File Size</th>
              <th>Owner Name</th>
              <th>Created</th>
              <th>Updated</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((m) => {
              const isSubscribed = subscriptions[m.Id];
              const isSubLoading = subscriptionLoading[m.Id];
              return (
                <tr key={m.Id}>
                  <td>
                    <input
                      type="checkbox"
                      aria-label={`Select model ${m.Id}`}
                      checked={selectedIds.has(m.Id)}
                      onChange={() => toggleSelected(m.Id)}
                    />
                  </td>
                  <td>{m.Id}</td>
                  <td>{m.Name || "-"}</td>
                  <td>{m.AutomaticExecution ? "Yes" : "No"}</td>
                  <td>{m.Public ? "Yes" : "No"}</td>
                  <td style={{ width: '300px', wordWrap: 'anywhere' }}>{m.FileName}</td>
                  <td>{(m.SignalTags || []).join(", ") || "-"}</td>
                  <td>{m.ActiveSignalTag || "-"}</td>
                  <td>{m.getFormattedFileSize()}</td>
                  <td>{m.OwnerName || "-"}</td>
                  <td>{m.getFormattedCreateTime()}</td>
                  <td>{m.getFormattedUpdateTime()}</td>
                  <td>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        className={`btn btn-sm ${isSubscribed ? 'btn-warning' : 'btn-outline-secondary'}`}
                        onClick={() => toggleSubscription(m)}
                        disabled={isSubLoading}
                        title={isSubscribed ? 'Unsubscribe from notifications' : 'Subscribe to notifications'}
                      >
                        {isSubLoading ? '...' : (
                          <>
                            <span role="img" aria-label="bell">🔔</span>
                          </>
                        )}
                      </button>
                      <button
                        className="btn btn-sm btn-primary"
                        onClick={() => openEditModal(m)}
                      >
                        Edit
                      </button>
                      <button
                        className="btn btn-sm btn-success"
                        onClick={() => downloadModel(m)}
                      >
                        Download
                      </button>
                      <button
                        className="btn btn-sm btn-danger"
                        onClick={() => deleteModel(m)}
                      >
                        Delete
                      </button>
                      {m.DefaultPerformanceCalculationId != null && (
                        <button
                          className="btn btn-sm btn-outline-info"
                          onClick={() => setPerfChartModel({ id: m.Id, name: m.Name || m.FileName, tag: m.ActiveSignalTag })}
                          title="Show performance chart"
                        >
                          Perf Chart
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </>
    );
  }

  function openEditModal(model) {
    setEditingModel(model);
    setEditName(model.Name || "");
    setEditDescription(model.Description || "");
    setEditAutomaticExecution(model.AutomaticExecution || false);
    setEditSignalTags(model.SignalTags || []);
    setEditActiveSignalTag(model.ActiveSignalTag || "");
    setEditFileName("");
    setEditFileData(null);
    setEditPublic(model.Public || false);
    setEditVisibleToUserIds(model.VisibleToUserIds || []);
    setEditRealtimeCandlesAutomaticExecutionIntervalInMinutes(
      model.RealtimeCandlesAutomaticExecutionIntervalInMinutes != null
        ? String(model.RealtimeCandlesAutomaticExecutionIntervalInMinutes)
        : ""
    );
    setEditCombineMode(model.SignalCombineMode || "CbSoM");
    setIsCreating(false);
  }

  function openCreateModal() {
    setEditingModel(null);
    setEditName("");
    setEditDescription("");
    setEditAutomaticExecution(false);
    setEditSignalTags([]);
    setEditActiveSignalTag("");
    setEditFileName("");
    setEditFileData(null);
    setEditPublic(false);
    setEditVisibleToUserIds([]);
    setEditRealtimeCandlesAutomaticExecutionIntervalInMinutes("");
    setEditCombineMode("CbSoM");
    setIsCreating(true);
  }

  function closeModal() {
    setEditingModel(null);
    setIsCreating(false);
    setSaving(false);
  }

  async function saveModel() {
    if (isCreating && !editFileData) {
      alert("File is required for new model");
      return;
    }

    const body = {
      Name: editName.trim() || null,
      Description: editDescription.trim() || null,
      AutomaticExecution: editAutomaticExecution,
      ActiveSignalTag: editActiveSignalTag.trim() || null,
      Public: editPublic,
      VisibleToUserIds: editVisibleToUserIds.length > 0 ? editVisibleToUserIds : null,
      RealtimeCandlesAutomaticExecutionIntervalInMinutes:
        editRealtimeCandlesAutomaticExecutionIntervalInMinutes !== ""
          ? parseInt(editRealtimeCandlesAutomaticExecutionIntervalInMinutes, 10) || 0
          : null,
      SignalCombineMode: editCombineMode,
    };

    if (!isCreating && editingModel) {
      body.Id = editingModel.Id;
    }

    if (editFileData) {
      body.FileData = editFileData;
      body.FileName = editFileName;
    }

    setSaving(true);
    try {
      const result = await ApiClient.saveModel(body);

      if (isCreating) {
        // Add new model to the list
        setItems(prev => [result, ...prev]);
        setTotal(prev => prev + 1);
      } else {
        // Update existing model in the list
        setItems(prev => prev.map(m => m.Id === result.Id ? result : m));
      }

      closeModal();
    } catch (err) {
      console.error("Save failed", err);
      alert("Save failed: " + (err.message || err));
      setSaving(false);
    }
  }

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div>
      <h1>Models</h1>
      <form onSubmit={onSearch} className="mb-3">
        <div style={{ maxWidth: "900px" }}>
          <div className="row">
            <div className="col-md-3 mb-2">
              <label className="col-form-label">Name (partial)</label>
              <input
                placeholder="Name"
                className="form-control"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
            <div className="col-md-3 mb-2">
              <label className="col-form-label">File Name (partial)</label>
              <input
                placeholder="File Name"
                className="form-control"
                value={fileName}
                onChange={(e) => setFileName(e.target.value)}
              />
            </div>
            <div className="col-md-3 mb-2">
              <label className="col-form-label">Realtime Candles Interval</label>
              <select
                className="form-control"
                value={hasRealtimeCandlesInterval}
                onChange={(e) => setHasRealtimeCandlesInterval(e.target.value)}
              >
                <option value="">&lt;Any&gt;</option>
                <option value="true">Yes</option>
                <option value="false">No</option>
              </select>
            </div>
            <div className="col-md-3 mb-2">
              <label className="col-form-label">Order By</label>
              <select
                className="form-control"
                value={orderBy}
                onChange={(e) => {
                  setOrderBy(e.target.value);
                  setPage(0);
                }}
              >
                {orderByOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

          </div>

          <div className="row">
            <div className="col-md-12 mb-2 d-flex align-items-end" style={{ gap: "8px", flexWrap: "wrap" }}>
              <button className="btn btn-primary" type="submit">
                Search
              </button>
              <button
                className="btn btn-success"
                type="button"
                onClick={openCreateModal}
              >
                Create New Model
              </button>
              <button
                className="btn btn-outline-primary"
                type="button"
                onClick={() => setImportModalOpen(true)}
                title="Import models from a zip created by the Export action"
              >
                Import
              </button>
            </div>
          </div>
        </div>
      </form>

      <div className="mb-2 d-flex align-items-center">
        <div className="mr-3">Page size:</div>
        <select
          className="form-control mr-3"
          style={{ width: "120px" }}
          value={pageSize}
          onChange={(e) => {
            setPageSize(Number(e.target.value));
            setPage(0);
          }}
        >
          {pageSizeOptions.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <div>
          Page: {page + 1} / {totalPages || 1}
        </div>
      </div>

      <div className="mb-3 d-flex" style={{ gap: "8px", alignItems: "center" }}>
        <button
          className="btn btn-secondary mr-2"
          disabled={page <= 0}
          onClick={() => setPage((p) => Math.max(0, p - 1))}
        >
          Previous
        </button>
        <button
          className="btn btn-secondary"
          disabled={page + 1 >= totalPages}
          onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
        >
          Next
        </button>
      </div>

      {/* Bulk-export toolbar — visible only when at least one model is
          selected. Lives outside the table so it sticks to the top of
          the results block and never gets pushed off-screen when the
          table is scrolled. */}
      <div
        className="alert alert-info py-2 px-3 mb-3 d-flex align-items-center"
        style={{ gap: "12px", display: selectedIds.size > 0 ? 'flex' : 'none' }}
      >
        <span className="me-2">
          <strong>{selectedIds.size}</strong> model{selectedIds.size === 1 ? '' : 's'} selected
        </span>
        <button
          className="btn btn-sm btn-success"
          onClick={bulkExportSelected}
          disabled={bulkExporting || selectedIds.size === 0}
        >
          {bulkExporting ? 'Exporting...' : 'Export'}
        </button>
        <button
          className="btn btn-sm btn-outline-secondary"
          onClick={() => setSelectedIds(new Set())}
          disabled={bulkExporting}
        >
          Clear selection
        </button>
      </div>

      {renderTable()}

      <div className="mt-2 d-flex" style={{ gap: "8px" }}>
        <button
          className="btn btn-secondary mr-2"
          disabled={page <= 0}
          onClick={() => setPage((p) => Math.max(0, p - 1))}
        >
          Previous
        </button>
        <button
          className="btn btn-secondary"
          disabled={page + 1 >= totalPages}
          onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
        >
          Next
        </button>
      </div>

      {/* Edit/Create modal */}
      {(editingModel || isCreating) && (
        <div
          className="modal show"
          tabIndex="-1"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {isCreating ? "Create New Model" : `Edit Model #${editingModel?.Id}`}
                </h5>
              </div>
              <div className="modal-body">
                {!isCreating && editingModel && editingModel.OwnerName && (
                  <div className="form-group">
                    <label>Owner Name</label>
                    <input
                      className="form-control"
                      value={editingModel.OwnerName}
                      disabled
                      readOnly
                    />
                  </div>
                )}
                <div className="form-group">
                  <label>Name</label>
                  <input
                    className="form-control"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    placeholder="Enter model name (optional)"
                  />
                </div>
                <div className="form-group">
                  <label>Description</label>
                  <textarea
                    className="form-control"
                    value={editDescription}
                    onChange={(e) => setEditDescription(e.target.value)}
                    placeholder="Enter model description (optional)"
                    rows="3"
                  />
                </div>
                <fieldset className="border rounded p-3 mb-3" style={{ marginTop: "1em" }}>
                  <legend className="w-auto px-2 mb-0" style={{ fontSize: "0.9rem", fontWeight: 600 }}>Automatic execution</legend>
                  <hr/>
                  <div className="form-group mb-2">
                    <label className="d-flex align-items-center">
                      <input
                        type="checkbox"
                        checked={editAutomaticExecution}
                        onChange={(e) => setEditAutomaticExecution(e.target.checked)}
                        className="mr-2"
                      />
                      &nbsp;Enable daily automatic execution
                    </label>
                  </div>
                  <div className="form-group mb-0">
                    <div className="d-flex align-items-center" style={{ gap: "12px" }}>
                      <label className="mb-0 text-nowrap">Realtime Candles Automatic Execution Interval (in minutes)</label>
                      <input
                        type="number"
                        className="form-control"
                        style={{ width: "160px" }}
                        min="1"
                        value={editRealtimeCandlesAutomaticExecutionIntervalInMinutes}
                        onChange={(e) => setEditRealtimeCandlesAutomaticExecutionIntervalInMinutes(e.target.value)}
                        placeholder="Leave empty to disable"
                      />
                    </div>
                    <small className="form-text text-muted">
                      If set, the model will be executed automatically using realtime candles at this interval during trading hours.
                      Set to empty to disable.
                    </small>
                  </div>
                </fieldset>
                <div className="form-group">
                  <label className="d-flex align-items-center">
                    <input
                      type="checkbox"
                      checked={editPublic}
                      onChange={(e) => setEditPublic(e.target.checked)}
                      className="mr-2"
                    />
                    &nbsp;Public
                  </label>
                </div>
                <div className="form-group">
                  <label>Visible to Users</label>
                  <select
                    multiple
                    className="form-control"
                    value={editVisibleToUserIds}
                    onChange={(e) => {
                      const selectedOptions = Array.from(e.target.selectedOptions, option => parseInt(option.value, 10));
                      setEditVisibleToUserIds(selectedOptions);
                    }}
                    style={{ height: '120px' }}
                  >
                    {usersList.map((user) => (
                      <option key={user.Id} value={user.Id}>
                        {user.Name} ({user.Email})
                      </option>
                    ))}
                  </select>
                  <small className="form-text text-muted">
                    Hold Ctrl/Cmd to select multiple users. Leave empty for no specific user restrictions.
                  </small>
                </div>
                <div className="form-group">
                  <label>Signal Tags</label>
                  <input
                    className="form-control"
                    value={(editSignalTags || []).join(", ") || "(none)"}
                    readOnly
                    disabled
                  />
                  <small className="form-text text-muted">Read from uploaded .sd2 file.</small>
                </div>
                <div className="form-group">
                  <label>Active Signal Tag</label>
                  <input
                    className="form-control"
                    list="signal-tags-list"
                    value={editActiveSignalTag}
                    onChange={(e) => setEditActiveSignalTag(e.target.value)}
                    placeholder="Select or type a signal tag"
                  />
                  <datalist id="signal-tags-list">
                    {(editSignalTags || []).map((tag) => (
                      <option key={tag} value={tag} />
                    ))}
                  </datalist>
                  <small className="form-text text-muted">Pick from Signal Tags or enter a custom value.</small>
                </div>
                <div className="form-group">
                  <label>Signal Combine Mode</label>
                  <select
                    className="form-control"
                    value={editCombineMode}
                    onChange={(e) => setEditCombineMode(e.target.value)}
                  >
                    {COMBINE_MODE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <small className="form-text text-muted">
                    Strategy used when this model's signals are combined with signals from other models.
                    Stored on the model and can be overridden per request by the consumer (e.g. performance-calculation page).
                  </small>
                </div>
                <div className="form-group">
                  <label>File {isCreating ? "*" : "(optional - leave empty to keep current file)"}</label>
                  <input
                    type="file"
                    className="form-control-file"
                    onChange={handleFileChange}
                  />
                  {editFileName && (
                    <small className="form-text text-muted">
                      Selected: {editFileName}
                    </small>
                  )}
                </div>
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-secondary"
                  onClick={closeModal}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  onClick={saveModel}
                  disabled={saving}
                >
                  {saving ? "Saving..." : (isCreating ? "Create" : "Update")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {perfChartModel && (
        <PerformanceChartModal
          modelId={perfChartModel.id}
          modelName={perfChartModel.name}
          tag={perfChartModel.tag}
          onClose={() => setPerfChartModel(null)}
        />
      )}

      {importModalOpen && (
        <ImportZipModal
          onClose={() => setImportModalOpen(false)}
          onImported={() => {
            // Refresh the page-1 result set so newly-imported rows
            // show up without the user having to re-run Search.
            setPage(0);
            fetchData();
          }}
        />
      )}
    </div>
  );
};

export default Models;